import React, { useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { AI_BUNDLE_ACCEPT } from '../aiDocumentFileTypes';

interface Props {
    isMatching: boolean;
    error?: string;
    onFilesSelected: (files: File[]) => void;
}

const describeSelection = (files: File[]) => {
    if (files.length === 1) return files[0].name;
    const zipCount = files.filter(f => f.name.toLowerCase().endsWith('.zip')).length;
    return zipCount > 0 ? `${files.length} files (${zipCount} zip)` : `${files.length} files`;
};

const RuntimeBundleDropzone: React.FC<Props> = ({ isMatching, error, onFilesSelected }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [selectionLabel, setSelectionLabel] = useState<string>('');

    const accept = (fileList?: FileList | null) => {
        const files = fileList ? Array.from(fileList).filter(f => f.size > 0) : [];
        if (files.length === 0) return;
        setSelectionLabel(describeSelection(files));
        onFilesSelected(files);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (!isMatching) accept(e.dataTransfer.files);
    };

    if (isMatching) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 bg-white rounded border shadow-sm">
                <Spinner animation="border" variant="success" className="mb-3" />
                <h6 className="fw-bold text-dark mb-1">Staging {selectionLabel || 'documents'} and matching them…</h6>
                <p className="small text-muted mb-0">The AI is reading file names and assigning each document to a review phase.</p>
            </div>
        );
    }

    return (
        <>
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className="d-flex flex-column align-items-center justify-content-center text-center rounded p-5"
                style={{
                    cursor: 'pointer',
                    border: `2px dashed ${isDragging ? '#198754' : '#adb5bd'}`,
                    backgroundColor: isDragging ? '#e8f5ee' : '#ffffff',
                    transition: 'all 0.15s ease-in-out'
                }}
            >
                <FaCloudUploadAlt size={46} className={isDragging ? 'text-success mb-3' : 'text-secondary mb-3'} />
                <h6 className="fw-bold text-dark mb-1">Drop a zip file or individual files</h6>
                <p className="small text-muted mb-2">
                    or click to browse. Each document will be auto-matched to the right input for you to confirm.
                </p>
                <p className="small text-muted mb-0" style={{ fontSize: '11px' }}>
                    PDF, Word, images, email, HTML, text, and zip · up to 50 documents
                </p>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={AI_BUNDLE_ACCEPT}
                    className="d-none"
                    onChange={(e) => {
                        accept(e.target.files);
                        e.target.value = '';
                    }}
                />
            </div>
            {error && (
                <div className="alert alert-danger border-0 shadow-sm small mt-3 mb-0">{error}</div>
            )}
        </>
    );
};

export default RuntimeBundleDropzone;
